import { Link } from 'react-router';
import {
  ArrowsClockwise,
  ChartBar,
  ClipboardText,
  Gear,
  House,
  ListChecks,
  SignOut,
  Trophy,
  UsersThree,
} from '@phosphor-icons/react';
import type { AppShellProps, NavItem } from '../screens/contracts';
import { BrandMark, cx } from './ui';

/**
 * The Taste shell.
 *
 * Bottom tab bar on phones — thumbs live at the bottom of the screen, and this app is
 * used one-handed on a couch. Desktop promotes the same items into a top rail rather than
 * inventing a second navigation model, so muscle memory transfers between the two.
 */

const ICONS: Record<NavItem['icon'], typeof House> = {
  home: House,
  picks: ListChecks,
  standings: Trophy,
  results: ChartBar,
  history: ClipboardText,
  roster: UsersThree,
  commish: Gear,
  settings: Gear,
};

export function AppShell({
  children,
  nav,
  user,
  leagueName,
  weekLabel,
  onLogout,
  updateReady,
  onApplyUpdate,
}: AppShellProps) {
  return (
    <div className="taste-root flex min-h-[100dvh] flex-col bg-field-900">
      {/* Header ------------------------------------------------------------ */}
      <header className="taste-safe-top sticky top-0 z-30 border-b border-line bg-nav/85 backdrop-blur-xl">
        <div className="mx-auto flex h-14 w-full max-w-6xl items-center gap-3 px-4">
          <Link to={nav[0]?.href ?? '/'} className="flex min-w-0 items-center gap-2.5">
            <BrandMark />
            <span className="truncate text-[15px] font-semibold tracking-tight text-field-100">
              {leagueName ?? "Otoe Pick'em"}
            </span>
          </Link>

          {weekLabel && (
            <span className="rounded-md bg-field-800 px-2 py-1 font-mono text-[11px] font-medium uppercase tracking-[0.14em] text-field-300">
              {weekLabel}
            </span>
          )}

          <div className="ml-auto flex items-center gap-1">
            {/* Desktop nav: same destinations, different container. */}
            <nav className="mr-2 hidden items-center gap-0.5 md:flex">
              {nav.map((item) => (
                <NavLinkItem key={item.key} item={item} orientation="horizontal" />
              ))}
            </nav>

            <Link
              to="/settings"
              aria-label="Settings"
              className="grid size-9 place-items-center rounded-lg text-field-400 transition-colors hover:bg-field-800 hover:text-field-100"
            >
              <Gear size={19} weight="regular" />
            </Link>
            <button
              onClick={onLogout}
              aria-label="Sign out"
              className="grid size-9 place-items-center rounded-lg text-field-400 transition-colors hover:bg-field-800 hover:text-field-100"
            >
              <SignOut size={19} weight="regular" />
            </button>
          </div>
        </div>

        {updateReady && (
          <button
            onClick={onApplyUpdate}
            className="flex w-full items-center justify-center gap-2 border-t border-signal/25 bg-signal/10 py-2 text-[13px] text-signal transition-colors hover:bg-signal/15"
          >
            <ArrowsClockwise size={14} weight="bold" />
            A new version is ready — tap to reload
          </button>
        )}
      </header>

      {/* Content ----------------------------------------------------------- */}
      <main className="mx-auto w-full max-w-6xl flex-1 px-4 pb-28 pt-5 md:pb-12">{children}</main>

      {/* Bottom tab bar ---------------------------------------------------- */}
      <nav className="taste-safe-bottom fixed inset-x-0 bottom-0 z-30 border-t border-line bg-nav/92 backdrop-blur-xl md:hidden">
        <div className="mx-auto flex max-w-md items-stretch justify-around px-1 py-1.5">
          {nav.map((item) => (
            <NavLinkItem key={item.key} item={item} orientation="vertical" />
          ))}
        </div>
      </nav>

      {user && (
        <div className="sr-only" aria-live="polite">
          Signed in as {user.displayName}
        </div>
      )}
    </div>
  );
}

function NavLinkItem({
  item,
  orientation,
}: {
  item: NavItem;
  orientation: 'horizontal' | 'vertical';
}) {
  const Icon = ICONS[item.icon] ?? House;

  if (orientation === 'horizontal') {
    return (
      <Link
        to={item.href}
        className={cx(
          'rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-200',
          item.isActive ? 'bg-field-800 text-field-100' : 'text-field-400 hover:text-field-100',
        )}
      >
        {item.label}
      </Link>
    );
  }

  return (
    <Link
      to={item.href}
      aria-current={item.isActive ? 'page' : undefined}
      className={cx(
        'relative flex min-w-0 flex-1 flex-col items-center gap-1 rounded-xl px-1 py-2',
        'transition-all duration-200 ease-[var(--ease-out-expo)] active:scale-[0.94]',
        item.isActive ? 'text-signal' : 'text-field-500',
      )}
    >
      {/* Active marker sits above the icon, so it never collides with the label. */}
      <span
        className={cx(
          'absolute inset-x-4 top-0 h-0.5 rounded-full transition-opacity duration-200',
          item.isActive ? 'bg-signal opacity-100' : 'opacity-0',
        )}
      />
      <Icon size={21} weight={item.isActive ? 'fill' : 'regular'} />
      <span className="text-[10px] font-medium tracking-tight">{item.label}</span>
      {item.badge != null && item.badge > 0 && (
        <span className="absolute right-3 top-1 grid size-4 place-items-center rounded-full bg-signal font-mono text-[9px] font-semibold text-ink">
          {item.badge}
        </span>
      )}
    </Link>
  );
}
